(() => {
  if (customElements.get('dynamic-table')) return;
  class DynamicTable extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.list = this.querySelector('[data-tablist]');
      this.stage = this.querySelector('[data-panels]');
      this.tabbed = this.dataset.tabs === 'true' && this.tabs.length > 1 && !!this.list;
      this.width = this.clientWidth;
      const update = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.measure());
      };
      this.observer = new ResizeObserver(update);
      this.observer.observe(this);
      this.querySelectorAll('.dt-scroll,.dt-table').forEach(el => this.observer.observe(el));
      window.addEventListener('resize', update, { signal });
      this.addEventListener('load', update, { signal, capture: true });
      if (this.tabbed) {
        this.list.setAttribute('role', 'tablist');
        this.tabs.forEach(tab => tab.setAttribute('role', 'tab'));
        this.panels.forEach(panel => {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', `${this.id}-tab-${panel.dataset.panel}`);
          panel.tabIndex = 0;
        });
        this.list.addEventListener('click', event => {
          const tab = event.target.closest('[data-tab]');
          if (!tab || !this.tabs.includes(tab)) return;
          event.preventDefault();
          // Stop the host theme's generic fragment/smooth-scroll handler.
          event.stopImmediatePropagation();
          this.activate(tab.dataset.tab);
        }, { signal, capture: true });
        this.list.addEventListener('keydown', event => {
          const index = this.tabs.indexOf(event.target.closest('[data-tab]'));
          if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End', ' '].includes(event.key)) return;
          event.preventDefault();
          event.stopPropagation();
          const rtl = getComputedStyle(this.list).direction === 'rtl';
          let next = index;
          if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.tabs.length - 1;
          else if (event.key !== ' ') next = (index + ((event.key === 'ArrowRight') !== rtl ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.activate(this.tabs[next].dataset.tab, true);
        }, { signal });
        document.addEventListener('shopify:block:select', event => {
          if (!(event.target instanceof Element) || !this.contains(event.target)) return;
          const panel = event.target.closest('[data-panel]');
          if (panel) this.activate(panel.dataset.panel);
          const appearance = event.target.closest('[data-appearance-slot]');
          if (appearance) this.activate(appearance.dataset.appearanceSlot);
        }, { signal });
        window.addEventListener('hashchange', () => this.selectHash(), { signal });
        this.activate(this.tabs[0].dataset.tab);
        this.selectHash();
      }
      this.dataset.enhanced = 'true';
      this.measure();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }
    reserveHeight() {
      if (!this.tabbed || !this.stage) return;
      const height = Math.ceil(this.stage.getBoundingClientRect().height);
      if (height > (parseFloat(this.stage.style.minHeight) || 0)) this.stage.style.minHeight = `${height}px`;
    }
    measure() {
      if (this.width !== this.clientWidth) {
        this.width = this.clientWidth;
        if (this.stage) this.stage.style.minHeight = '';
      }
      this.querySelectorAll('.dt-scroll').forEach(scroller => {
        const hint = document.getElementById(scroller.dataset.scrollHint);
        const overflow = scroller.clientWidth > 0 && scroller.scrollWidth > scroller.clientWidth + 1;
        if (hint) hint.hidden = !overflow;
        if (overflow) scroller.setAttribute('aria-describedby', scroller.dataset.scrollHint);
        else scroller.removeAttribute('aria-describedby');
      });
      this.reserveHeight();
    }
    selectHash() {
      let target;
      try { target = document.getElementById(decodeURIComponent(location.hash.slice(1))); } catch { return; }
      if (!target || !this.contains(target)) return;
      const panel = target.closest('[data-panel]');
      if (panel) this.activate(panel.dataset.panel);
    }
    activate(slot, focus = false) {
      const selected = this.tabs.find(tab => tab.dataset.tab === slot);
      if (!selected) return;
      // Retain the largest visited panel so shorter tables cannot move the page's scroll anchor.
      this.reserveHeight();
      this.tabs.forEach(tab => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.setAttribute('aria-controls', `${this.id}-panel-${tab.dataset.tab}`);
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.panel !== slot; });
      if (focus) selected.focus({ preventScroll: true });
      this.measure();
    }
  }
  customElements.define('dynamic-table', DynamicTable);
})();
